'use client';

import { Badge, Button, Flex, Input } from '@chakra-ui/react';
import { memo, useCallback, useState } from 'react';

interface Props {
  name: string;
  isDirty: boolean;
  onNameChange: (name: string) => void;
  onSave: () => void;
  onDiscard: () => void;
}

const WorkflowHeader = ({ name, isDirty, onNameChange, onSave, onDiscard }: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(name);

  const handleStartEdit = useCallback(() => {
    setEditValue(name);
    setIsEditing(true);
  }, [name]);

  const handleFinishEdit = useCallback(() => {
    setIsEditing(false);
    if (editValue.trim() !== '') {
      onNameChange(editValue.trim());
    }
  }, [editValue, onNameChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {handleFinishEdit();}
      if (e.key === 'Escape') {
        setIsEditing(false);
        setEditValue(name);
      }
    },
    [handleFinishEdit, name],
  );

  return (
    <Flex
      h="48px"
      px={4}
      alignItems="center"
      justifyContent="space-between"
      borderBottomWidth="1px"
      borderBottomColor="border.light"
      bg="surface.background"
      flexShrink={0}
    >
      <Flex alignItems="center" gap={3}>
        {isEditing ? (
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleFinishEdit}
            onKeyDown={handleKeyDown}
            size="sm"
            fontSize="sm"
            fontWeight="medium"
            variant="flushed"
            autoFocus
            w="250px"
            borderColor="border.light"
          />
        ) : (
          <Flex
            fontSize="sm"
            fontWeight="medium"
            cursor="pointer"
            px={1}
            py={0.5}
            borderRadius="md"
            _hover={{ bg: 'surface.hover' }}
            onClick={handleStartEdit}
          >
            {name}
          </Flex>
        )}
        {isDirty && (
          <Badge
            size="xs"
            px={2}
            borderRadius="full"
            bg="yellow.100"
            color="yellow.700"
            fontSize="2xs"
          >
            Unsaved
          </Badge>
        )}
      </Flex>
      <Flex gap={2}>
        <Button
          size="2xs"
          variant="secondaryOutline"
          borderRadius="md"
          onClick={onDiscard}
          disabled={!isDirty}
        >
          Discard
        </Button>
        <Button
          size="2xs"
          borderRadius="md"
          onClick={onSave}
          disabled={!isDirty}
        >
          Save
        </Button>
      </Flex>
    </Flex>
  );
};

export default memo(WorkflowHeader);
